import React, { Suspense, lazy } from 'react';
import LandingPage from './pages/LandingPage';
import About from './pages/About';
import Footer from './components/Footer';

const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const DeleteAccount = lazy(() => import('./pages/DeleteAccount'));
const CheckoutComplete = lazy(() => import('./pages/CheckoutComplete'));
const ReferralLanding = lazy(() => import('./pages/ReferralLanding'));

// Every route serves the same index.html, whose baked-in tags describe the
// landing page. Fix up title/canonical/robots per route once React knows the
// path. Search engines executing JS (Google, Bing) pick these up; the
// noindex'd routes are also blocked in robots.txt.
const ROUTE_META: Record<string, { title: string; canonical?: string; noindex?: boolean }> = {
  '/': { title: 'Peckle: AI Product Ad Maker | Phone Photo to Pro Post', canonical: 'https://peckle.ai/' },
  '/about': { title: 'About Peckle | Gotok Technologies Limited', canonical: 'https://peckle.ai/about' },
  '/terms': { title: 'Terms of Service | Peckle', canonical: 'https://peckle.ai/terms' },
  '/privacy': { title: 'Privacy Policy | Peckle', canonical: 'https://peckle.ai/privacy' },
  '/delete': { title: 'Delete Your Account | Peckle', canonical: 'https://peckle.ai/delete' },
  '/checkout/complete': { title: 'Payment Complete | Peckle', noindex: true },
};

function applyRouteMeta(path: string) {
  const meta = ROUTE_META[path] ?? (path.startsWith('/l') ? { title: 'Peckle', noindex: true } : ROUTE_META['/']);
  document.title = meta.title;

  const canonicalEl = document.querySelector('link[rel="canonical"]');
  if (meta.canonical) {
    canonicalEl?.setAttribute('href', meta.canonical);
  } else {
    canonicalEl?.remove();
  }

  if (meta.noindex) {
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', 'noindex');
  }
}

const LegalPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<div className="min-h-screen bg-white dark:bg-zinc-950" />}>
    {children}
    <Footer />
  </Suspense>
);

const App: React.FC = () => {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';

  // The pre-rendered index.html (scripts/prerender.mjs) marks non-landing
  // routes with data-boot-loader so CSS shows the boot loader instead of the
  // baked-in landing markup. Once React has committed the real page, lift the
  // flag — before the browser paints, hence layout effect.
  React.useLayoutEffect(() => {
    document.documentElement.removeAttribute('data-boot-loader');
    document.documentElement.removeAttribute('data-prerender-route');
    applyRouteMeta(path);
  }, [path]);

  if (path === '/about') {
    return (
      <>
        <About />
        <Footer />
      </>
    );
  }

  if (path === '/terms') {
    return <LegalPage><Terms /></LegalPage>;
  }

  if (path === '/privacy') {
    return <LegalPage><Privacy /></LegalPage>;
  }

  if (path === '/checkout/complete') {
    return (
      <Suspense fallback={<div className="min-h-screen bg-white dark:bg-zinc-950" />}>
        <CheckoutComplete />
      </Suspense>
    );
  }

  if (path === '/delete') {
    return <LegalPage><DeleteAccount /></LegalPage>;
  }

  // Personal referral links (/l/{code}). Android is normally 302'd to the
  // Play Store at the CloudFront edge before reaching the SPA; this page
  // covers iOS/desktop and malformed codes.
  if (path.startsWith('/l/') || path === '/l') {
    return (
      <Suspense fallback={<div className="min-h-screen bg-white dark:bg-zinc-950" />}>
        <ReferralLanding />
      </Suspense>
    );
  }

  return <LandingPage />;
};

export default App;
